<template>
    <Modal size="md" @close="$emit('close')">
        <template #header>
            <div class="text-gray-600">Card setup link</div>
        </template>

        <template #body>
            <div class="p-6">
                <template v-if="!checkoutUrl">
                    <div class="text-sm font-medium text-gray-800">
                        Subscription type
                    </div>

                    <div class="mt-3 flex gap-3">
                        <AtomicButton
                            :color="type === 'pro' ? 'yellow' : 'white'"
                            size="md"
                            @click="type = 'pro'"
                        >
                            Pro
                        </AtomicButton>

                        <AtomicButton
                            :color="type === 'plugin' ? 'yellow' : 'white'"
                            size="md"
                            @click="type = 'plugin'"
                        >
                            Plugin
                        </AtomicButton>
                    </div>

                    <p class="mt-4 text-sm text-gray-500">
                        Generates a Stripe Checkout link the customer can open to
                        add a new card to their {{ type }} billing.
                    </p>
                </template>

                <template v-else>
                    <div class="text-sm font-medium text-gray-800">
                        Share this link with the customer:
                    </div>

                    <div class="mt-3 flex items-start gap-2">
                        <div
                            class="grow break-all rounded-[6px] border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600"
                        >
                            {{ checkoutUrl }}
                        </div>

                        <CopyToClipboardButton :code="checkoutUrl" />
                    </div>

                    <a
                        :href="checkoutUrl"
                        target="_blank"
                        rel="noopener"
                        class="mt-3 inline-block text-sm text-topol-blue-500 hover:text-topol-blue-600"
                    >
                        Open link
                    </a>
                </template>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end border-t border-gray-200 p-6">
                <AtomicButton
                    v-if="!checkoutUrl"
                    color="white"
                    size="md"
                    :disabled="loading"
                    @click="generate"
                >
                    {{ loading ? 'Generating...' : 'Generate link' }}
                </AtomicButton>

                <AtomicButton
                    size="md"
                    color="yellow"
                    class="ml-2"
                    @click="$emit('close')"
                >
                    Close
                </AtomicButton>
            </div>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/components/Atomic/Modals/Modal.vue';
import AtomicButton from '@/components/Atomic/Buttons/AtomicButton.vue';
import CopyToClipboardButton from '@/components/Atomic/Miscellaneous/CopyToClipboardButton.vue';
import axios from 'axios';
import { route } from 'ziggy-js';
import { create } from '@/composables/useNotifications';
import ICurrentTeam from '@/types/Members/ICurrentTeam';

const props = defineProps<{
    team: ICurrentTeam;
}>();

defineEmits(['close']);

const type = ref<'pro' | 'plugin'>('pro');
const checkoutUrl = ref('');
const loading = ref(false);

const generate = async () => {
    loading.value = true;

    try {
        const response = await axios.post(
            route('kiosk.teams.card-setup-link.store', { team: props.team.id }),
            { type: type.value }
        );

        checkoutUrl.value = response.data.data.url;
    } catch (e) {
        const message =
            axios.isAxiosError(e) && e.response?.data?.message
                ? e.response.data.message
                : 'There was an error creating the card setup link.';

        create({
            title: 'Could not create link',
            text: message,
            type: 'error',
        });
    } finally {
        loading.value = false;
    }
};
</script>
